import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { ChangeRequestStore, RequestDto, RequestStatus } from 'stores/requests/requests-store';
import { EventFormStore } from 'stores/events/event-form-store';
import TailwindRejectionReasonEditor from 'widgets/bussiness/rejection-reason-editor-tw';
import TailwindRejectedByEditor from 'widgets/bussiness/rejected-by-editor-tw';
import { RejectProperties } from 'site/pages/shared/events-and-requests/reject-modal';
import TailwindMessage from 'site/pages/landing-pages/mobile/alerts/message-tw';
import { ItemReference } from 'stores/dataStore';

export interface EventFormStoreProps extends WithTranslation {
  open: boolean;
  eventFormStore?: EventFormStore;
  changeRequestStore?: ChangeRequestStore;
  cancelRequest?: boolean;
  request?: RequestDto;
  onCancel?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
}

interface TailwindCancelEventPopupState extends RejectProperties {
  loading: boolean;
  errMsgs: string[];
}

@connect(['eventFormStore', EventFormStore], ['changeRequestStore', ChangeRequestStore])
class TailwindCancelEventPopup extends React.Component<EventFormStoreProps, TailwindCancelEventPopupState> {
  static defaultProps = { onCancel: () => {}, onSuccess: () => {}, onError: () => {}, cancelRequest: false };
  state: TailwindCancelEventPopupState = { rejectReason: '', rejectedById: '', rejectionReasonId: '', loading: false, errMsgs: [] };

  private cancelEvent = async (requestStatus: RequestStatus, cancelRequest: boolean = this.props.cancelRequest) => {
    const { rejectReason, rejectedById, rejectionReasonId } = this.state;
    const errMsgs = [];
    const { onSuccess, onError, eventFormStore, changeRequestStore, request } = this.props;
    const { item } = eventFormStore.state;

    if (!rejectReason && !item.isDirectEvent) errMsgs.push('Invalid Reject reason text');
    if (!rejectionReasonId && !item.isDirectEvent) errMsgs.push('Is required to select Reject Reason');
    if (!rejectedById && !item.isDirectEvent) errMsgs.push('Is required to select Rejected By');

    if (errMsgs.length) {
      this.setState({ errMsgs });
      return;
    }
    this.setState({ loading: true });
    try {
      let response: any;
      if (cancelRequest) {
        response = await changeRequestStore.changeStatus(request?.id, requestStatus, rejectReason, rejectedById, rejectionReasonId);
      } else {
        response = await eventFormStore.cancelEvent(requestStatus, rejectReason, rejectedById, rejectionReasonId);
      }
      if (response?.isSuccess || response?.id) onSuccess();
    } catch (error) {
      console.error(error);
      onError();
    }
    this.setState({ loading: false });
  };

  private onRejectRequest = () => this.cancelEvent(RequestStatus.Rejected);

  private rejectionReasonIdHandler = (rejectionReason: ItemReference) => {
    this.setState({ rejectionReasonId: rejectionReason.id });
  };

  private rejectReasonHandler = rejectReason => {
    this.setState({ rejectReason: rejectReason });
  };

  private onPendingRequest = () => this.cancelEvent(RequestStatus.Pending);

  private rejectedByHandler = (rejectedBy: ItemReference) => {
    this.setState({ rejectedById: rejectedBy.id });
  };

  private onDismiss = () => this.setState({ errMsgs: [] });

  private onCancel = () => this.props.onCancel();

  public render() {
    const { t, eventFormStore } = this.props;
    const { rejectionReasonId, rejectedById, rejectReason, errMsgs } = this.state;
    const { item } = eventFormStore.state;
    const subtitle = item?.hasRequests
      ? 'What do you want to do with the ASSOCIATED REQUESTS?'
      : 'This action is not reversible, are you sure do you want to cancel it?';
    const rejectBtn = item?.hasRequests ? 'Reject Request' : 'Cancel Event';
    const errors = errMsgs.length ? (
      <TailwindMessage
        error
        icon="exclamation-circle"
        header={t('An error ocurred')}
        list={errMsgs}
        onDismiss={this.onDismiss}
        className="mb-4"
      />
    ) : null;

    return (
      <>
        {/* Fondo semitransparente con desenfoque */}
        <div
          className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-50 backdrop-blur-sm"
          style={{
            paddingLeft: '56px', // espacio para la toolbar izquierda
            paddingRight: '16px' // espacio para scrollbar derecha
          }}
        >
          {/* Contenedor del modal con borde y sombreado */}
          <div className="bg-white rounded-lg border border-gray-300 shadow-2xl w-full max-w-sm p-4 relative">
            {/* Botón Back */}
            <button onClick={this.onCancel} className="flex items-center gap-1 text-sm text-black-600 mb-2">
              ← {t('Back')}
            </button>

            {/* Errores */}
            {errors}

            <h3 className="text-lg font-semibold mb-4">{t('You are going to CANCEL the event.')}</h3>

            {/* Selectores */}
            <div className="mb-3">
              <label className="block text-sm text-gray-700 mb-1">{t('Select Reject Reason')}</label>
              <TailwindRejectionReasonEditor value={rejectionReasonId} onChange={this.rejectionReasonIdHandler} />
            </div>

            <div className="mb-3">
              <label className="block text-sm text-gray-700 mb-1">{t('Select Rejected By')}</label>
              <TailwindRejectedByEditor value={rejectedById} onChange={this.rejectedByHandler} />
            </div>

            <div className="mb-3">
              <label className="block text-sm text-gray-700 mb-1">{t('Reason')}</label>
              <textarea
                className="w-full p-2 border border-gray-300 rounded"
                onChange={e => this.rejectReasonHandler(e.target.value)}
                value={rejectReason}
                placeholder={t('Reason')}
                rows={4}
              />
            </div>

            <p className="block text-sm text-gray-700 mb-1">{t(subtitle)}</p>

            {/* Botones */}
            <button className="w-full bg-gray-200 text-black py-2 rounded mt-2" onClick={this.onCancel}>
              {t('Back')}
            </button>

            <button className="w-full bg-primary-600 text-white py-2 rounded mt-2" onClick={this.onRejectRequest}>
              {t(rejectBtn)}
            </button>

            {item.hasRequests && (
              <button className="w-full bg-primary-600 text-white py-2 rounded mt-2" onClick={this.onPendingRequest}>
                {t('Return to Pending')}
              </button>
            )}
          </div>
        </div>
      </>
    );
  }
}

export default withTranslation()(TailwindCancelEventPopup);
